<template>
  <div class="attachments-form" @paste="pasteImage">
    <!-- Camera view finder -->
    <div v-if="showVideo">
      <video
        ref="video"
        class="camera-video-container"
        autoplay
        playsinline
      ></video>
      <button
        class="button camera-close-button"
        @click.prevent="showVideo = false"
      >
        <i class="fas fa-times"></i>
      </button>
      <button
        class="button camera-capture-button"
        @click.prevent="captureImage"
      ></button>
    </div>
    <form @submit.prevent="getFile" enctype="multipart/form-data">
      <div
        v-if="message"
        :class="`message ${
          error ? 'is-danger' : info ? 'is-info' : 'is-success'
        }`"
      >
        <button class="delete" @click="clearMessage"></button>
        <div class="message-body">{{ message }}</div>
      </div>
      <div class="container">
        <b-row>
          <b-col>
            <!-- Drop zone (camera) -->
            <div class="dropzone">
              <!-- Call to action button -->
              <div
                :disabled="!uploading"
                class="call-to-action"
                @click="showCameraView"
              >
                <span class="file-icon"> <i class="fas fa-camera"></i> </span>
                <span> Camera </span>
              </div>
            </div>
          </b-col>
          <b-col>
            <!-- Drop zone (choose file) -->
            <div class="dropzone">
              <input
                type="file"
                ref="file"
                @change="getFile"
                class="input-file"
                accept=".gif,.jpg,.jpeg,.png"
                :disabled="disabled"
              />
              <!-- Call to action button -->
              <div :disabled="!uploading" class="call-to-action">
                <span class="file-icon"> <i class="fas fa-upload"></i> </span>
                <span> Choose file... </span>
              </div>
            </div>
          </b-col>
          <b-col>
            <!-- Drop zone (paste file) -->
            <div
              v-if="supportsCB"
              class="dropzone"
              @click="pasteImage"
              :disabled="disabled"
            >
              <a ref="file-paste" class="input-file" />
              <!-- Call to action button -->
              <div
                :disabled="!uploading || disabled"
                class="call-to-action call-to-action-title"
              >
                <span class="file-icon"> <i class="fas fa-paste"></i> </span>
                <span> Paste... </span>
              </div>
            </div>
          </b-col>
        </b-row>

        <!-- Content -->
        <section>
          <div class="content is-vcentered has-text-centered">
            <div class="columns is-multiline">
              <!-- Attached items -->
              <div
                v-for="(file, index) in attachments"
                :key="file.thumbnailKey"
                class="column is-one-quarter"
              >
                <div
                  class="box attached-item"
                  name="attached-item"
                  @click="enlargeImage(index)"
                >
                  <!-- Delete button -->
                  <button
                    class="button is-small is-transparent delete-button"
                    @click.prevent="removeItem(file.thumbnailKey)"
                  >
                    <span class="icon">
                      <i class="fas fa-times"></i>
                    </span>
                  </button>
                  <!-- Image -->
                  <BaseEnlargeableImage
                    ref="enlargeImage"
                    class="item-image"
                    :src="file.thumbnailUrl"
                    :src_large="file.fullUrl"
                  />
                  <!-- Title -->
                  <div
                    v-if="file.originalFilename"
                    class="item-title is-italic"
                    id="imageTitleId"
                  >
                    {{ file.originalFilename }}
                  </div>
                  <b-tooltip target="imageTitleId" variant="secondary">{{
                    file.originalFilename
                  }}</b-tooltip>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </form>
  </div>
</template>

<script>
import { httpClient, httpClientNoContentType } from "../api/http-client";
import _ from "lodash";
import config from "../../config";
import { nextTick } from "vue";

export default {
  components: {},
  props: {
    namespace: { type: String, default: "" },
    recId: {
      type: [String, Number],
      default: -1,
    },
    tenantId: {
      type: [String],
      required: true,
    },
    clientId: {
      type: [String],
      required: true,
    },
    clientName: {
      type: [String],
      required: true,
    },
    processorName: {
      type: [String],
      required: true,
    },
    disabled: Boolean,
  },
  data() {
    return {
      showVideo: false,
      capturedImage: null,
      imageCaptured: false,
      message: "",
      error: false,
      info: false,
      progress: 0,
      uploading: false,
      uploadedFiles: [],
      supportsCB: false,
      pastedFile: null,
      filesAdded: [],
      filesDeleted: [],
      removingItem: false,
    };
  },
  created() {
    if (navigator.clipboard) {
      this.supportsCB = true;
    }
  },
  mounted() {
    nextTick(() => {});
    //this.startCamera();
  },
  computed: {
    baseUrl() {
      return `${process.env.VUE_APP_BASE_URL}`;
    },
    attachments() {
      return this.$store.getters[`${this.namespace}/attachments`];
    },
    //     attachments() {
    //       const attachments = this.$store.getters[`${this.namespace}/attachments`];
    //       if (!attachments) {
    //         return [];
    //       } else {
    //         return attachments;
    //       }
    //     }
  },
  methods: {
    showCameraView() {
      this.startCamera();
      this.showVideo = true;
    },
    // Capture camera image
    async startCamera() {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: true,
        });
        this.$refs.video.srcObject = stream;
      } catch (error) {
        console.error("Error accessing camera:", error);
        alert("Unable to access the camera.");
      }
    },
    async captureImage() {
      const video = this.$refs.video;
      const canvas = document.createElement("canvas");
      const context = canvas.getContext("2d");
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      context.drawImage(video, 0, 0, canvas.width, canvas.height);
      // Convert canvas to a Blob
      const blob = await new Promise((resolve) =>
        canvas.toBlob(resolve, "image/png")
      );
      // Create a File object from the Blob
      const filename = this.createUniqueFilename("camera", "png");
      const file = new File([blob], filename, {
        type: "image/png",
        lastModified: Date.now(),
      });
      // Send file to server and show on screen
      this.showVideo = false;
      await this.sendFile(file);
      this.imageCaptured = true; // Toggle to show the image
    },

    createUniqueFilename(namepart, extension) {
      const now = new Date();
      const formattedDate = now
        .toISOString()
        .replace("T", "_") // Replace 'T' with '_'
        .split(".")[0] // Remove milliseconds
        .replace(/[-:]/g, ""); // Remove '-' and ':'
      return `${namepart}-${formattedDate}.${extension}`;
    },

    clearMessage() {
      this.message = "";
      this.error = false;
      this.info = false;
    },
    enlargeImage(index) {
      if (this.removingItem) return;
      if (this.$refs.enlargeImage[index].state === "enlarged") return;
      this.$refs.enlargeImage[index].enlarge();
    },
    attachmentsChanged() {
      // Rase event
      this.$emit("attachmentsChanged", true);
      // Set hasAttachments
      console.log(
        "in BaseDropZone, attachmentsChanged, this.attachments: ",
        this.attachments
      );
      if (this.attachments && this.attachments.length > 0) {
        this.$store.commit(`${this.namespace}/HAS_ATTACHMENTS`, true);
      } else {
        this.$store.commit(`${this.namespace}/HAS_ATTACHMENTS`, false);
      }
    },

    async pasteImage() {
      try {
        const clipboardItems = await navigator.clipboard.read();
        for (const clipboardItem of clipboardItems) {
          for (const type of clipboardItem.types) {
            const blob = await clipboardItem.getType(type);
            // we can now use blob here
            if (type.includes("image")) {
              // Image: convert to file
              const extension = type.split("/")[1];
              const filename = this.createUniqueFilename("pasted", extension);
              this.pastedFile = new File([blob], filename, {
                type: type,
                lastModified: Date.now(),
              });
            }
          }
          if (!this.pastedFile) {
            this.message = "No image in clipboard to paste";
            this.info = true;
            setTimeout(this.clearMessage, 5000); // Auto-dismiss after 5 seconds
            return;
          }
          // Send file to server and show on screen
          await this.sendFile(this.pastedFile);
          this.error = false;
          this.info = false;
          this.message = "";
        }
      } catch (err) {
        if (err.message.includes("No valid data on clipboard")) {
          this.info = true;
        } else {
          this.error = true;
        }
        this.message = err.message;
        console.error(err.name, err.message);
      }
    },

    async removeItem(thumbnailKey) {
      this.removingItem = true;
      // Remove from fileserver
      const file = this.attachments.find(
        (x) => x.thumbnailKey === thumbnailKey
      );
      if (!file) {
        throw new Error(
          `Error removing attachment. File not found (${thumbnailKey}).`
        );
      }
      // Save file info in case we cancel save
      this.uploading = true;
      this.filesDeleted.push(file);
      await this.deleteFile(file);
      this.uploading = false;
      this.$store.commit(
        `${this.namespace}/ATTACHMENT_REMOVE`,
        file.thumbnailKey
      );
      this.attachmentsChanged();
      this.removingItem = false;
    },

    bucketMeta() {
      return {
        "x-bucketMeta-id": this.recId,
        "x-bucketMeta-tenant-id": this.$props.tenantId,
        "x-bucketMeta-client-id": this.$props.clientId,
        "x-bucketMeta-client-name": this.$props.clientName,
        "x-bucketMeta-processor-name": this.$props.processorName,
      };
    },

    // Call API to delete s3 file (both full and thumbnail are deleted)
    // Note this is a soft delete (moves file to s3 deleted bucket)
    async deleteFile(file) {
      const url = `${process.env.VUE_APP_BASE_URL}/issue/dropzone/`;
      await httpClient.delete(url, {
        data: file,
        headers: this.bucketMeta(),
      });
    },

    // Call API to restore (undelete) s3 file (api does both full and thumbnail)
    async restoreFile(file) {
      const url = `${process.env.VUE_APP_BASE_URL}/issue/dropzone/restore`;
      await httpClient.post(url, {
        data: file,
        headers: this.bucketMeta(),
      });
    },

    validate(file) {
      const maxSize = config.fileUploads.MAX_SIZE;
      const allowedTypes = config.fileUploads.FILE_TYPES;
      if (file?.size > maxSize) {
        return `File is too large. Max size is ${maxSize / 1000 / 100}MB`;
      }
      if (!allowedTypes.includes(file.type)) {
        return "Only images allowed";
      }
      return "";
    },

    async getFile(e) {
      e.preventDefault();
      // Pre-validate files from file input dropzone
      this.message = "";
      this.error = false;
      this.info = false;
      const files = [this.$refs.file.files[0]];
      _.forEach(files, (file) => {
        if (file) {
          const validateMessage = this.validate(file);
          if (validateMessage !== "") {
            // At least one file is in error: Stop
            this.message = validateMessage;
            this.error = true;
            return;
          }
        }
      });
      if (this.message !== "") return;
      await this.sendFile(this.$refs.file.files[0]);
    },

    async sendFile(file) {
      // Stop if filename already in uploadedFiles
      if (
        this.attachments?.filter((x) => x.originalFilename === file.name)
          .length > 0
      ) {
        return;
      }
      this.error = false;
      this.message = "";
      // Send file to server
      let formData = new FormData();
      formData.append("file", file);
      const url = `${process.env.VUE_APP_BASE_URL}/issue/dropzone`;
      try {
        this.uploading = true;
        const res = await httpClientNoContentType.post(url, formData, {
          headers: { ...this.bucketMeta() },
          onUploadProgress: (e) =>
            (this.progress = Math.round((e.loaded * 100) / e.total)),
        });
        const fileObj = {
          thumbnailUrl: res.data.thumbnailUrl,
          thumbnailKey: res.data.thumbnailKey,
          fullUrl: res.data.fullUrl,
          fullKey: res.data.fullKey,
          originalFilename: res.data.originalFilename,
        };
        // Save in case we cancel
        this.filesAdded.push(fileObj);
        // Save in store attachments
        this.$store.commit(`${this.namespace}/ATTACHMENT_ADD`, fileObj);
        this.attachmentsChanged();
        this.uploading = false;
      } catch (err) {
        this.message = err.data.error;
        this.error = true;
        this.uploading = false;
      }
    },
  },
};
</script>

<style lang="css" scoped src="../../node_modules/bulma/css/bulma.css"></style>

<style lang="css" scoped>
.dropzone {
  margin-right: -5px;
  margin-left: -5px;
  height: 100px;
  padding: 15px;
  margin-bottom: 15px;
  position: relative;
  cursor: pointer;
  outline: 2px dashed gray;
  outline-offset: -10px;
  background-color: #eee;
  border-radius: 10px;
  transition: all 0.08s ease-in;
}
.input-file {
  opacity: 0;
  width: 100%;
  height: 100px;
  position: absolute;
  cursor: pointer;
}
.dropzone:hover {
  background-color: #ddd;
}
.dropzone .call-to-action {
  display: flex;
  justify-content: center;
  font-size: 1rem;
  padding: 25px;
}
.dropzone .progress-bar {
  text-align: center;
  padding: 60px 10px;
}
.file-icon {
  font-size: 1.5rem;
  color: #888;
}
.call-to-action-title {
  font-size: 0.5rem;
}
.click-to-paste {
  border-bottom-style: none;
  display: block;
  padding: 10px;
}
.item-image {
  margin-top: 0px;
  margin-bottom: 5px;
}
.item-title {
  font-size: 0.7rem;
}
.attachments-form {
  min-height: 300px;
  margin-bottom: 15px;
}
.attachments {
  display: flex;
  padding-left: 2px;
  padding-right: -5px;
}
.attachments .column {
  width: 49%;
  margin-left: 5px;
  margin-top: 10px;
}
.attached-item {
  box-shadow: rgba(100, 100, 111, 0.2) 0px 7px 29px 0px;
  position: relative;
  display: block;
  text-align: center;
  padding: 20px;
  height: 150px;
  background-color: rgb(231, 236, 240);
  border: 1px #ccc solid;
  border-radius: 5px;
  cursor: zoom-in;
}
.delete-button {
  position: absolute;
  background-color: transparent;
  border-color: transparent;
  top: 0px;
  right: 0px;
  margin: 2px;
  width: 10px;
  height: 20px;
  color: #aaa;
}
.caution-text {
  color: darkred;
  font-size: 0.8rem;
  animation: blink 0.8s 1 alternate;
}

.message .delete {
  float: right;
  background: none;
  border: none;
  font-size: 1.2rem;
  cursor: pointer;
}

.camera-video-container {
  position: absolute;
  top: -20px;
  left: 37px;
  width: 90%;
  height: auto;
  z-index: 999;
  border-radius: 10px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.3);
}

.camera-close-button {
  position: absolute;
  top: -10px;
  right: 45px;
  width: 30px;
  height: 30px;
  background-color: #eee;
  border: none;
  font-size: 16px;
  /* color: #aaa; */
  color: #222;
  z-index: 1001;
  cursor: pointer;
  padding: 0;
  margin: 0;
  transition: transform 0.2s ease, background-color 0.2s ease, color 0.2s ease;
}

.camera-close-button:hover {
  color: #666;
  background-color: #fff;
  transform: scale(1.2);
}

.camera-capture-button {
  position: absolute;
  bottom: -150px;
  left: 48%;
  width: 60px;
  height: 60px;
  background-color: white;
  border: none;
  border-radius: 50%;
  z-index: 1001;
  cursor: pointer;
  box-shadow: 0 0 0 5px rgba(255, 255, 255, 0.3),
    /* Outline effect */ 0 4px 6px rgba(0, 0, 0, 0.2); /* Subtle shadow for depth */
  transition: transform 0.2s ease;
}

.camera-capture-button:hover {
  transform: scale(1.2);
}

@keyframes blink {
  0% {
    color: darkred;
  }
  50% {
    color: #ccc;
  }
  100% {
    color: darkred;
  }
}

/* @keyframes blink {
  from {
    color: white;
  }
  to {
    color: darkred;
  }
} */
</style>
